import {Button, Divider, IconButton, Stack, Switch, Typography} from '@mui/material';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages';
import {translations} from 'presentation/translations-new';
import React, {Fragment, PropsWithChildren} from 'react';
import {
	ContentDrawer,
	FooterDrawer,
	HeaderDrawer,
	Puller,
	PullerContainer,
	StyledButton,
	StyledIcon,
	StyledStackSwitch,
	StyledSwipeableDrawer,
	StyledTypographySwitch,
	TitleDrawer,
} from './styles';
import {
	CategoryListProps,
	CategoryListSwitchProps,
	DrawerFooterProps,
	DrawerHeaderProps,
	DrawerRootProps,
	type DrawerContentProps,
} from './types';
import {Loading} from '../Loading/Loading';

const {clear, apply} = translations['pt-br'].commons;

function DrawerRoot({
	children,
	width,
	customMaxHeight,
	height,
	pullerPosition,
	pullerPadding,
	customMaxWidth,
	disableSwipeToOpen,
	...drawerProps
}: PropsWithChildren<DrawerRootProps>) {
	const {isMobile} = useMediaQueries();

	const swipeAreaWidthValue = isMobile && !disableSwipeToOpen ? 20 : 0;
	const shouldShowPuller = isMobile && (drawerProps.open || !disableSwipeToOpen);

	return (
		<StyledSwipeableDrawer
			anchor={isMobile ? 'bottom' : 'right'}
			$customWidth={width}
			$customMaxWidth={customMaxWidth}
			$customMaxHeight={customMaxHeight}
			$customHeight={height}
			swipeAreaWidth={swipeAreaWidthValue}
			disableSwipeToOpen={disableSwipeToOpen}
			{...drawerProps}>
			{shouldShowPuller ? (
				<PullerContainer position={pullerPosition} $pullerPadding={pullerPadding}>
					<Puller />
				</PullerContainer>
			) : null}
			{children}
		</StyledSwipeableDrawer>
	);
}

function DrawerHeader({
	title,
	onClose,
	fontSize,
	fontWeight,
	lineHeight,
	containerPadding,
	closeBtnDataTagId,
}: DrawerHeaderProps) {
	return (
		<HeaderDrawer $padding={containerPadding}>
			<TitleDrawer $fontSize={fontSize} $fontWeight={fontWeight} $lineHeight={lineHeight}>
				{title}
			</TitleDrawer>
			<IconButton data-tag-id={closeBtnDataTagId} size="small" onClick={onClose} edge="end">
				<Icon name="close-default" />
			</IconButton>
		</HeaderDrawer>
	);
}

function DrawerContent({children, ...stackProps}: DrawerContentProps) {
	return <ContentDrawer {...stackProps}>{children}</ContentDrawer>;
}

function DrawerContentListByCategory({categoryList, sx}: CategoryListProps) {
	return (
		<DrawerContent
			sx={{
				display: 'flex',
				flexDirection: 'column',
				overflow: 'auto',
				gap: 2,
				...sx,
			}}>
			{categoryList.map(({categoryTitle, categoryItemsList}, categoryIndex) => (
				<Stack key={categoryIndex}>
					<Typography variant="body1" fontWeight={600} color="text.secondary" mb={1}>
						{categoryTitle}
					</Typography>
					<Stack flexDirection="row" flexWrap="wrap" gap={1}>
						{categoryItemsList.map((item, index) => (
							<StyledButton
								data-tag-id={item?.dataTagId}
								key={index}
								onClick={item.onClick}
								variant="outlined"
								$isSelected={item.active}>
								{item.title}
							</StyledButton>
						))}
					</Stack>
				</Stack>
			))}
		</DrawerContent>
	);
}

function DrawerContentListByCategorySwitch({categoryList, dataTagId}: CategoryListSwitchProps) {
	return (
		<DrawerContent sx={{display: 'flex', flexDirection: 'column'}}>
			{categoryList.map(({categoryTitle, categoryItemsList}, categoryIndex) => (
				<Fragment key={categoryIndex}>
					<Typography variant="body1" fontWeight={600} color="text.secondary" mb={1}>
						{categoryTitle}
					</Typography>
					<Stack gap={1}>
						{categoryItemsList.map((item, index) => (
							<StyledStackSwitch key={index}>
								<StyledIcon name={item.icon} />
								<StyledTypographySwitch>{item.title}</StyledTypographySwitch>
								<Switch
									checked={item.check}
									onChange={item.onCheck}
									data-tag-id={dataTagId}
								/>
							</StyledStackSwitch>
						))}
					</Stack>
				</Fragment>
			))}
		</DrawerContent>
	);
}

function DrawerFooter({
	buttonSize = 'large',
	firstButtonTitle,
	firstButtonOnClick,
	firstButtonDataTagId,
	firstButtonDisabled,
	firstButtonIsLoading,
	firstButtonColor,
	hideFirstButton,
	secondButtonTitle,
	secondButtonOnClick,
	secondButtonDataTagId,
	canStacks = false,
	secondButtonDisabled,
	secondButtonIsLoading,
	secondButtonColor,
	hideSecondButton,
	children,
	direction = 'row',
	hideDivider = false,
}: DrawerFooterProps) {
	return (
		<Stack>
			{hideDivider ? null : <Divider sx={{mb: 2}} />}

			<Stack px={2} pb={2} gap={2}>
				{children}
				<FooterDrawer
					direction={canStacks ? {xs: 'column-reverse', sm: direction} : direction}>
					{hideFirstButton ? null : (
						<Button
							variant="outlined"
							onClick={firstButtonOnClick}
							fullWidth
							size={buttonSize}
							disabled={firstButtonDisabled}
							data-tag-id={firstButtonDataTagId}
							color={firstButtonColor}
							endIcon={
								firstButtonIsLoading ? <Loading size={16} color="inherit" /> : null
							}>
							{firstButtonTitle ?? clear}
						</Button>
					)}
					{hideSecondButton ? null : (
						<Button
							variant="contained"
							onClick={secondButtonOnClick}
							fullWidth
							size={buttonSize}
							disabled={secondButtonDisabled}
							data-tag-id={secondButtonDataTagId}
							color={secondButtonColor}
							endIcon={
								secondButtonIsLoading ? <Loading size={16} color="inherit" /> : null
							}>
							{secondButtonTitle ?? apply}
						</Button>
					)}
				</FooterDrawer>
			</Stack>
		</Stack>
	);
}

export const Drawer = {
	Root: DrawerRoot,
	Header: DrawerHeader,
	Content: DrawerContent,
	ContentListByCategory: DrawerContentListByCategory,
	ContentListByCategorySwitch: DrawerContentListByCategorySwitch,
	Footer: DrawerFooter,
};
